import React from "react";
import { Select, Popover, PopoverTrigger, PopoverContent, PopoverBody, Button, Input, Box, VStack, useDisclosure } from "@chakra-ui/react";

const DynamicSelectInput = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  condition,
  isDisabled,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Conditionally render simple Select if "condition" is true
  if (condition) {
    return (
      <Select
        backgroundColor="white"
        border="1px solid gray"
        borderColor="black"
        borderRadius={4}
        mb="12px"
        fontSize="xl"
        size="lg"
        autoComplete="off"
        onChange={onChange}
        value={value}
        required
        isDisabled={isDisabled}
        userSelect="none"
        placeholder={placeholder}
        _hover={{
          outline: "none",
        }}
        _active={{
          bg: "white",
          color: "#2fb5b0",
          outline: "none",
        }}
      >
        <option value={value}>{value}</option>
      </Select>
    );
  }

  // Default to Searchable Popover for all other cases
  const filteredOptions = options.filter((opt) =>
    opt?.value?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      matchWidth
      isLazy
      autoFocus={false}
    >
      <PopoverTrigger>
        <Button
          w="100%"
          backgroundColor="white"
          border="1px solid gray"
          borderColor="black"
          borderRadius={4}
          mb="12px"
          fontSize="xl"
          h="36px"
          fontWeight="normal"
          justifyContent="space-between"
          textAlign="left"
          px={4}
          isDisabled={isDisabled}
          _hover={{ outline: "none" }}
          _active={{ bg: "white", outline: "none" }}
          rightIcon={<Box as="span" fontSize="sm">▼</Box>}
        >
          <Box isTruncated>
            {value || placeholder}
          </Box>
        </Button>
      </PopoverTrigger>
      <PopoverContent w="100%" p={0} maxW="unset">
        <PopoverBody p={2}>
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            mb={2}
            autoFocus
          />
          <Box maxH="250px" overflowY="auto">
            {filteredOptions.length > 0 ? (
              <VStack align="stretch" spacing={0}>
                {filteredOptions.map((opt, index) => (
                  <Box
                    key={index}
                    p={3}
                    cursor="pointer"
                    bg={value === opt?.value ? "gray.100" : "white"}
                    _hover={{ bg: "gray.100" }}
                    onClick={() => {
                      onChange({ target: { value: opt?.value } });
                      onClose();
                      setSearchQuery("");
                    }}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    {opt?.value}
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box p={3} color="gray.500" textAlign="center">No options found</Box>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default DynamicSelectInput;

export const DynamicSelectInputEvents = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  condition,
  isDisabled,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (condition) {
    return (
      <Select
        backgroundColor="white"
        border="1px solid gray"
        borderRadius={4}
        py={3}
        mb="0px"
        fontSize="14px"
        size="md"
        w="fit-content"
        autoComplete="off"
        onChange={onChange}
        value={value}
        required
        isDisabled={isDisabled}
        userSelect="none"
        placeholder={placeholder}
        _hover={{
          outline: "none",
        }}
        _active={{
          bg: "white",
          color: "#2fb5b0",
          outline: "none",
        }}
      >
        <option defaultValue={value?.trim}>{value}</option>
      </Select>
    );
  }

  const filteredOptions = options.filter((opt) =>
    opt?.value?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      matchWidth
      isLazy
      autoFocus={false}
    >
      <PopoverTrigger>
        <Button
          w="fit-content"
          minW="150px"
          backgroundColor="white"
          border="1px solid gray"
          borderRadius={4}
          py={3}
          mb="0px"
          fontSize="14px"
          size="md"
          fontWeight="normal"
          justifyContent="space-between"
          textAlign="left"
          px={4}
          isDisabled={isDisabled}
          _hover={{ outline: "none" }}
          _active={{ bg: "white", outline: "none" }}
          rightIcon={<Box as="span" fontSize="xs">▼</Box>}
        >
          <Box isTruncated>
            {value || placeholder}
          </Box>
        </Button>
      </PopoverTrigger>
      <PopoverContent w="250px" p={0} maxW="unset">
        <PopoverBody p={2}>
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            mb={2}
            autoFocus
            size="sm"
          />
          <Box maxH="200px" overflowY="auto">
            {filteredOptions.length > 0 ? (
              <VStack align="stretch" spacing={0}>
                {filteredOptions.map((opt, index) => (
                  <Box
                    key={index}
                    p={2}
                    cursor="pointer"
                    bg={value === opt?.value?.trim() ? "gray.100" : "white"}
                    _hover={{ bg: "gray.100" }}
                    onClick={() => {
                      // DynamicSelectInputEvents often expects trimmed values or specific format
                      // Check original implementation: value={opt?.value?.trim()}
                      // So we pass the value back.
                      onChange({ target: { value: opt?.value?.trim() } });
                      onClose();
                      setSearchQuery("");
                    }}
                    fontSize="14px"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    {opt?.value}
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box p={2} color="gray.500" textAlign="center" fontSize="14px">No options found</Box>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
